import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import api from '../lib/api';
import socketManager from '../lib/socket';
import {
  Search,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  ExternalLink,
  Loader2,
  BarChart3,
  ShoppingCart,
  X,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Link,
  Glasses,
  Zap,
  Star,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react';

function parseMaybeJsonArray(value, fallback) {
  if (!value) return fallback;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function parseClobTokenIds(value, market = null) {
  // Try the provided value first
  if (value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  
  // Fallback: try other field names from the market object
  if (market) {
    // Try different field name variations from Polymarket API
    const alternatives = [
      market.clobTokenIds,
      market.clob_token_ids,
      market.tokenIds,
      market.token_ids,
      market.tokens,
      // Single token markets
      market.tokenId ? [market.tokenId] : null,
      market.token_id ? [market.token_id] : null,
    ];
    
    for (const alt of alternatives) {
      if (alt) {
        if (Array.isArray(alt)) return alt.filter(Boolean);
        if (typeof alt === 'string') {
          return alt.split(',').map((s) => s.trim()).filter(Boolean);
        }
      }
    }
  }
  
  return [];
}

// Sports detection - same logic as Whale Mode
const SPORTS_KEYWORDS = [
  'nfl', 'nba', 'mlb', 'nhl', 'mls', 'ncaa', 'college football', 'college basketball',
  'super bowl', 'world series', 'stanley cup', 'march madness', 'playoffs', 'championship',
  'premier league', 'la liga', 'bundesliga', 'serie a', 'champions league', 'uefa', 'fifa',
  'tennis', 'wimbledon', 'us open', 'french open', 'australian open',
  'golf', 'pga', 'masters', 'ryder cup',
  'boxing', 'ufc', 'mma', 'fight',
  'f1', 'formula 1', 'nascar', 'racing',
  'olympics', 'world cup',
  'lakers', 'celtics', 'warriors', 'bulls', 'heat', 'knicks', 'nets', 'clippers',
  'yankees', 'dodgers', 'red sox', 'cubs', 'mets', 'giants', 'cardinals',
  'patriots', 'cowboys', 'eagles', 'chiefs', 'bills', '49ers', 'packers', 'ravens',
  'maple leafs', 'bruins', 'rangers', 'penguins', 'blackhawks', 'avalanche',
  'real madrid', 'barcelona', 'manchester', 'liverpool', 'chelsea', 'arsenal', 'tottenham',
  'bayern', 'juventus', 'psg', 'inter milan', 'ac milan'
];

function isSportsMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  for (const kw of SPORTS_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  
  // Team vs team pattern
  if (/\b[A-Z][a-z]+\s+vs\.?\s+[A-Z][a-z]+\b/i.test(market?.question || '')) {
    return true;
  }
  
  return false;
}

// Crypto detection - same logic as Whale Mode
const CRYPTO_KEYWORDS = [
  'bitcoin', 'btc', 'ethereum', 'eth', 'solana', 'sol', 'dogecoin', 'doge',
  'ripple', 'xrp', 'litecoin', 'ltc', 'cardano', 'ada', 'polkadot', 'dot',
  'chainlink', 'link', 'avalanche', 'avax', 'polygon', 'matic', 'uniswap', 'uni',
  'binance', 'bnb', 'crypto', 'cryptocurrency', 'token price', 'coin price'
];

function isCryptoMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  // Exclude false positives
  if (hay.includes('elon') && hay.includes('doge') && !hay.includes('dogecoin')) return false;
  if (hay.includes('government efficiency')) return false;
  if (hay.includes(' vs. ') && hay.includes('avalanche')) return false; // NHL
  
  for (const kw of CRYPTO_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  
  return false;
}

// AI detection
const AI_KEYWORDS = [
  'ai', 'artificial intelligence', 'chatgpt', 'openai', 'gpt-4', 'gpt-5', 'claude', 'anthropic',
  'gemini', 'bard', 'llm', 'large language model', 'machine learning', 'deep learning',
  'neural network', 'agi', 'superintelligence', 'midjourney', 'stable diffusion', 'dall-e',
  'copilot', 'github copilot', 'ai model', 'foundation model'
];

function isAIMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  for (const kw of AI_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

// Tech detection
const TECH_KEYWORDS = [
  'apple', 'google', 'microsoft', 'amazon', 'meta', 'facebook', 'twitter', 'x.com',
  'tesla', 'nvidia', 'amd', 'intel', 'samsung', 'iphone', 'android', 'ios',
  'spacex', 'starlink', 'neuralink', 'boring company', 'elon musk',
  'tiktok', 'snapchat', 'instagram', 'youtube', 'netflix', 'spotify',
  'uber', 'lyft', 'airbnb', 'doordash', 'instacart',
  'software', 'hardware', 'startup', 'ipo', 'tech company', 'silicon valley'
];

function isTechMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  // Exclude crypto markets from tech
  if (isCryptoMarket(market)) return false;
  
  for (const kw of TECH_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

// Politics detection
const POLITICS_KEYWORDS = [
  'trump', 'biden', 'harris', 'desantis', 'republican', 'democrat', 'gop',
  'congress', 'senate', 'house', 'president', 'election', 'vote', 'ballot',
  'governor', 'mayor', 'primary', 'caucus', 'nominee', 'cabinet', 'impeach',
  'supreme court', 'scotus', 'legislation', 'bill', 'law', 'executive order',
  'white house', 'capitol', 'political', 'party', 'campaign', 'poll'
];

function isPoliticsMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  for (const kw of POLITICS_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

// Finance/Economy detection
const FINANCE_KEYWORDS = [
  'stock', 'market', 's&p', 'nasdaq', 'dow jones', 'fed', 'federal reserve',
  'interest rate', 'inflation', 'gdp', 'recession', 'unemployment', 'jobs report',
  'earnings', 'revenue', 'profit', 'ipo', 'merger', 'acquisition',
  'bond', 'treasury', 'yield', 'forex', 'currency', 'dollar', 'euro', 'yen',
  'oil price', 'gold price', 'commodity', 'trade', 'tariff', 'economy'
];

function isFinanceMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  // Exclude crypto
  if (isCryptoMarket(market)) return false;
  
  for (const kw of FINANCE_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

// Geopolitics/World detection
const GEOPOLITICS_KEYWORDS = [
  'ukraine', 'russia', 'putin', 'zelensky', 'nato', 'china', 'taiwan', 'xi jinping',
  'iran', 'israel', 'gaza', 'palestine', 'hamas', 'hezbollah', 'netanyahu',
  'north korea', 'kim jong', 'syria', 'afghanistan', 'iraq', 'saudi', 'yemen',
  'war', 'invasion', 'military', 'troops', 'sanctions', 'nuclear', 'missile',
  'un', 'united nations', 'g7', 'g20', 'eu', 'european union', 'brexit',
  'middle east', 'asia', 'africa', 'latin america', 'south america'
];

function isGeopoliticsMarket(market, specificCategory) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  // Middle East specific
  if (specificCategory === 'middle-east') {
    const meKeywords = ['israel', 'gaza', 'palestine', 'iran', 'saudi', 'yemen', 'syria', 'iraq', 'lebanon', 'jordan', 'egypt', 'middle east', 'hamas', 'hezbollah', 'netanyahu'];
    return meKeywords.some(kw => hay.includes(kw));
  }
  
  for (const kw of GEOPOLITICS_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

// Pop Culture detection
const POP_CULTURE_KEYWORDS = [
  'movie', 'film', 'oscar', 'academy award', 'golden globe', 'emmy', 'grammy',
  'celebrity', 'kardashian', 'taylor swift', 'beyonce', 'drake', 'kanye',
  'netflix', 'hbo', 'disney', 'marvel', 'dc', 'star wars', 'game of thrones',
  'super bowl halftime', 'concert', 'tour', 'album', 'song', 'music',
  'tiktok viral', 'influencer', 'youtube', 'streamer', 'twitch',
  'dating', 'married', 'divorce', 'baby', 'scandal', 'award show'
];

function isPopCultureMarket(market) {
  const q = (market?.question || '').toLowerCase();
  const slug = (market?.slug || '').toLowerCase();
  const hay = `${q} ${slug}`;
  
  for (const kw of POP_CULTURE_KEYWORDS) {
    if (hay.includes(kw)) return true;
  }
  return false;
}

function TradingModal({ isOpen, onClose, market, outcome, tokenId, currentPrice }) {
  const [side, setSide] = useState('BUY');
  const [orderType, setOrderType] = useState('market');
  const [limitOrderType, setLimitOrderType] = useState('GTC'); // GTC or FOK
  const [amount, setAmount] = useState('');
  const safePrice = typeof currentPrice === 'number' && isFinite(currentPrice) ? currentPrice : 0.5;
  const [limitPrice, setLimitPrice] = useState(safePrice.toFixed(2));
  const [slippage, setSlippage] = useState(2);
  const [estimate, setEstimate] = useState(null);
  const [livePrice, setLivePrice] = useState(safePrice);
  
  // Update live price from WebSocket
  useEffect(() => {
    if (!tokenId) return;
    
    const handlePriceUpdate = (data) => {
      if (data.tokenId === tokenId && data.price !== undefined) {
        setLivePrice(data.price);
      }
    };
    
    socketManager.on('marketPrice', handlePriceUpdate);
    socketManager.subscribeToMarkets([tokenId]);
    
    return () => {
      socketManager.off('marketPrice', handlePriceUpdate);
    };
  }, [tokenId]);
  
  // Update limit price when current price changes
  useEffect(() => {
    if (livePrice && orderType === 'limit' && !limitPrice) {
      setLimitPrice(livePrice.toFixed(2));
    }
  }, [livePrice, orderType]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    if (amount && orderType === 'market') {
      estimateExecution();
    }
  }, [amount, side]);
  
  const estimateExecution = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    
    setIsEstimating(true);
    setError('');
    try {
      const response = await api.post('/trading/estimate', {
        tokenId,
        side,
        amount: parseFloat(amount)
      });
      setEstimate(response.data);
      if (response.data?.error) {
        setError(response.data.error);
      }
    } catch (err) {
      console.error('Estimate error:', err);
      const status = err.response?.status;
      if (status === 404) {
        setError('Market not found or has expired. Please refresh the page.');
      } else {
        setError(err.response?.data?.error || 'Failed to estimate trade');
      }
    } finally {
      setIsEstimating(false);
    }
  };
  
  const handleTrade = async () => {
    const numAmount = parseFloat(amount);
    const numLimitPrice = parseFloat(limitPrice);
    
    // Validation
    if (!amount || !Number.isFinite(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount (minimum $1)');
      return;
    }
    if (numAmount < 1) {
      setError('Minimum order is $1');
      return;
    }
    if (orderType === 'limit') {
      if (!limitPrice || !Number.isFinite(numLimitPrice) || numLimitPrice <= 0) {
        setError('Please enter a valid limit price');
        return;
      }
      if (numLimitPrice < 0.01 || numLimitPrice > 0.99) {
        setError('Limit price must be between $0.01 and $0.99');
        return;
      }
    }
    if (!tokenId || tokenId.length < 10) {
      setError('Invalid market - please refresh and try again');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      let response;
      
      if (orderType === 'market') {
        response = await api.post('/trading/market-order', {
          tokenId,
          side,
          amount: numAmount,
          slippage
        });
      } else {
        const shares = numAmount / numLimitPrice;
        if (shares < 0.01) {
          setError('Order size too small - increase amount or lower price');
          setIsLoading(false);
          return;
        }
        response = await api.post('/trading/limit-order', {
          tokenId,
          side,
          shares: Math.floor(shares * 100) / 100, // Round to 2 decimals
          price: Math.round(numLimitPrice * 100) / 100,
          orderType: limitOrderType
        });
      }
      
      if (response.data.success !== false) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setAmount('');
        }, 2000);
      } else {
        setError(response.data.errorMsg || 'Order failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place order');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-xl max-w-md w-full">
        <div className="p-4 border-b border-dark-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Trade {outcome}</h3>
            <p className="text-sm text-dark-400 truncate max-w-[300px]">{market?.question}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-dark-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold mb-2">Order Placed!</h3>
            <p className="text-dark-400">Your order has been submitted.</p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            {/* Side selector */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSide('BUY')}
                className={`py-3 rounded-lg font-medium transition-colors ${
                  side === 'BUY' 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
                }`}
              >
                Buy {outcome}
              </button>
              <button
                onClick={() => setSide('SELL')}
                className={`py-3 rounded-lg font-medium transition-colors ${
                  side === 'SELL' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-dark-700 text-dark-400 hover:bg-dark-600'
                }`}
              >
                Sell {outcome}
              </button>
            </div>
            
            {/* Order type */}
            <div className="flex gap-2">
              <button
                onClick={() => setOrderType('market')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  orderType === 'market' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
                }`}
              >
                Market
              </button>
              <button
                onClick={() => setOrderType('limit')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  orderType === 'limit' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
                }`}
              >
                Limit
              </button>
            </div>
            
            {/* Amount */}
            <div>
              <label className="label">Amount (USDC)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            
            {/* Limit price (for limit orders) */}
            {orderType === 'limit' && (
              <>
                <div>
                  <label className="label">Limit Price (current: ${(livePrice || safePrice).toFixed(2)})</label>
                  <input
                    type="number"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    className="input"
                    placeholder="0.01 - 0.99"
                    min="0.01"
                    max="0.99"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="label">Order Type</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setLimitOrderType('GTC')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        limitOrderType === 'GTC' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
                      }`}
                      title="Good-Till-Cancelled: Order stays in book until filled or cancelled"
                    >
                      GTC
                    </button>
                    <button
                      onClick={() => setLimitOrderType('FOK')}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        limitOrderType === 'FOK' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-dark-400'
                      }`}
                      title="Fill-Or-Kill: Must fill immediately or cancel"
                    >
                      FOK
                    </button>
                  </div>
                  <p className="text-xs text-dark-500 mt-1">
                    {limitOrderType === 'GTC' ? 'Order stays in book until filled' : 'Must fill immediately or cancel'}
                  </p>
                </div>
              </>
            )}
            
            {/* Slippage (for market orders) */}
            {orderType === 'market' && (
              <div>
                <label className="label">Max Slippage (%)</label>
                <input
                  type="number"
                  value={slippage}
                  onChange={(e) => setSlippage(parseFloat(e.target.value))}
                  className="input"
                  min="0.1"
                  max="50"
                  step="0.1"
                />
              </div>
            )}
            
            {/* Estimate */}
            {estimate && orderType === 'market' && (
              <div className="p-3 bg-dark-700/50 rounded-lg text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-dark-400">Est. Shares</span>
                  <span>{estimate.totalShares?.toFixed(4)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-400">Avg. Price</span>
                  <span>${estimate.avgPrice?.toFixed(4)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-dark-400">Est. Slippage</span>
                  <span className={estimate.slippage > slippage ? 'text-red-400' : ''}>
                    {estimate.slippage?.toFixed(2)}%
                  </span>
                </div>
              </div>
            )}
            
            {/* Submit */}
            <button
              onClick={handleTrade}
              disabled={isLoading || !amount}
              className={`btn w-full py-3 ${side === 'BUY' ? 'btn-success' : 'btn-danger'}`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Placing Order...
                </span>
              ) : (
                `${side} ${outcome}`
              )}
            </button>
            
            <p className="text-xs text-dark-500 text-center">
              {livePrice !== safePrice ? (
                <span className="text-primary-400">Live price: ${(livePrice || 0).toFixed(2)} ⚡</span>
              ) : (
                <span>Current price: ${safePrice.toFixed(2)}</span>
              )}
              {' • Trading on Polygon'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketCard({ market, onTrade, opportunityScore, gogglesMode }) {
  const [expanded, setExpanded] = useState(false);
  const [priceFlash, setPriceFlash] = useState({});
  const prevPricesRef = useRef(null);
  
  const outcomes = parseMaybeJsonArray(market.outcomes, ['Yes', 'No']);
  const prices = parseMaybeJsonArray(market.outcomePrices, [0.5, 0.5]);
  const tokens = parseClobTokenIds(market.clobTokenIds, market);
  
  // Flash animation when price changes
  useEffect(() => {
    if (!prevPricesRef.current) {
      prevPricesRef.current = prices;
      return;
    }
    
    const newFlash = {};
    prices.forEach((price, idx) => {
      const prevPrice = prevPricesRef.current[idx];
      if (prevPrice !== undefined && price !== prevPrice) {
        newFlash[idx] = price > prevPrice ? 'up' : 'down';
      }
    });
    
    if (Object.keys(newFlash).length > 0) {
      setPriceFlash(newFlash);
      setTimeout(() => setPriceFlash({}), 1000);
    }
    
    prevPricesRef.current = prices;
  }, [prices]);
  
  // Determine border color based on opportunity score in goggles mode
  const getBorderClass = () => {
    if (!gogglesMode || !opportunityScore) return '';
    if (opportunityScore.rating === 'hot') return 'ring-2 ring-amber-500';
    if (opportunityScore.rating === 'warm') return 'ring-1 ring-primary-500';
    return '';
  };
  
  return (
    <div className={`card card-hover ${getBorderClass()}`}>
      {/* Goggles Mode Opportunity Badge */}
      {gogglesMode && opportunityScore && opportunityScore.rating !== 'neutral' && (
        <div className={`flex items-center gap-2 mb-3 px-2 py-1 rounded-lg text-xs font-medium ${
          opportunityScore.rating === 'hot' 
            ? 'bg-amber-500/20 text-amber-400' 
            : 'bg-primary-500/20 text-primary-400'
        }`}>
          {opportunityScore.rating === 'hot' ? <Zap className="w-3 h-3" /> : <Star className="w-3 h-3" />}
          {opportunityScore.reason} (Score: {opportunityScore.score})
        </div>
      )}
      
      <div className="flex items-start gap-4">
        {market.icon && (
          <img
            src={market.icon}
            alt=""
            className="w-12 h-12 rounded-lg object-cover bg-dark-700"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium line-clamp-2">{market.question}</h3>
          <div className="flex items-center gap-3 mt-2 text-sm text-dark-400">
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              ${(market.volumeNum || 0).toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <BarChart3 className="w-3 h-3" />
              ${(market.liquidityNum || 0).toLocaleString()}
            </span>
            {market.endDate && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(market.endDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Outcome Prices with Trade Buttons - supports any number of outcomes */}
      <div className={`grid gap-3 mt-4 ${
        outcomes.length === 2 ? 'grid-cols-2' : 
        outcomes.length === 3 ? 'grid-cols-3' : 
        outcomes.length >= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'
      }`}>
        {outcomes.map((outcome, i) => {
          // Color scheme for multiple outcomes
          const colorSchemes = [
            { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
            { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400' },
            { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
            { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400' },
            { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400' },
            { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400' },
            { bg: 'bg-pink-500/10', border: 'border-pink-500/20', text: 'text-pink-400' },
            { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-400' },
          ];
          const colors = colorSchemes[i % colorSchemes.length];
          const price = parseFloat(prices[i]) || 0;
          
          return (
            <div
              key={outcome + i}
              className={`p-3 rounded-lg ${colors.bg} border ${colors.border}`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm text-dark-400 truncate" title={outcome}>{outcome}</p>
                {market.active && tokens[i] && (
                  <button
                    onClick={() => onTrade(market, outcome, tokens[i], price)}
                    className="p-1 hover:bg-white/10 rounded transition-colors flex-shrink-0"
                    title={`Trade ${outcome}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className={`text-lg font-bold ${colors.text} transition-all duration-300 ${
                priceFlash[i] === 'up' ? 'text-emerald-400 scale-110' : 
                priceFlash[i] === 'down' ? 'text-red-400 scale-110' : ''
              }`}>
                {(price * 100).toFixed(0)}¢
                {priceFlash[i] && (
                  <span className="ml-1 text-xs">
                    {priceFlash[i] === 'up' ? '↑' : '↓'}
                  </span>
                )}
              </p>
            </div>
          );
        })}
      </div>

      {/* Expand/Collapse for more info */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-center gap-1 w-full mt-3 py-2 text-sm text-dark-400 hover:text-white transition-colors"
      >
        {expanded ? 'Less' : 'More'}
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-dark-700 space-y-2 text-sm animate-fade-in">
          <div className="flex justify-between">
            <span className="text-dark-400">24h Volume</span>
            <span>${(market.volume24hr || 0).toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-dark-400">Liquidity</span>
            <span>${(market.liquidityNum || 0).toLocaleString()}</span>
          </div>
          {market.endDate && (
            <div className="flex justify-between">
              <span className="text-dark-400">End Date</span>
              <span>{new Date(market.endDate).toLocaleDateString()}</span>
            </div>
          )}
          <a
            href={`https://polymarket.com/event/${market.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 w-full mt-2 py-2 text-primary-400 hover:text-primary-300"
          >
            View on Polymarket <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* Status badge */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-700">
        <div className="flex items-center gap-2">
          <span className={`badge ${market.active ? 'badge-success' : 'badge-warning'}`}>
            {market.active ? 'Active' : 'Closed'}
          </span>
          {outcomes.length > 2 && (
            <span className="badge bg-purple-500/20 text-purple-400">
              {outcomes.length} outcomes
            </span>
          )}
        </div>
        {market.active && tokens.length > 0 && (
          <span className="text-xs text-dark-400">
            Click <ShoppingCart className="w-3 h-3 inline" /> on any outcome to trade
          </span>
        )}
      </div>
    </div>
  );
}

export default function Markets() {
  const { user } = useAuthStore();
  const [markets, setMarkets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('volume');
  const [expiryFilter, setExpiryFilter] = useState(''); // '', '4h', '24h', '7d'
  const [tradingModal, setTradingModal] = useState({ isOpen: false, market: null, outcome: null, tokenId: null, price: null });
  const [urlInput, setUrlInput] = useState('');
  const [gogglesMode, setGogglesMode] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [isTierLoading, setIsTierLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const autoRefreshRef = useRef(null);
  
  // Fetch subscription tier on mount (like WhaleVault does)
  useEffect(() => {
    let canceled = false;
    
    const fetchTier = async () => {
      setIsTierLoading(true);
      try {
        const res = await api.get('/payments/subscription');
        if (!canceled) {
          const tier = res.data?.effectiveTier || res.data?.tier || 'free';
          console.log('Markets: Subscription tier:', tier);
          setSubscriptionTier(tier);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        if (!canceled) {
          setSubscriptionTier('free');
        }
      } finally {
        if (!canceled) {
          setIsTierLoading(false);
        }
      }
    };
    
    fetchTier();
    return () => { canceled = true; };
  }, []);
  
  // Check if user has Basic tier or above for Goggles Mode
  const hasGogglesAccess = subscriptionTier && subscriptionTier !== 'free';

  useEffect(() => {
    fetchMarkets();
    
    // Auto-refresh every 60 seconds (reduced from 30s to prevent flicker)
    autoRefreshRef.current = setInterval(() => {
      fetchMarkets(true); // silent refresh
    }, 60000);
    
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [category, sortBy]);
  
  // Track WebSocket connection status
  useEffect(() => {
    const checkConnection = () => {
      setWsConnected(socketManager.isConnected?.() || false);
    };
    
    socketManager.on('connect', () => setWsConnected(true));
    socketManager.on('disconnect', () => setWsConnected(false));
    checkConnection();
    
    return () => {
      socketManager.off('connect');
      socketManager.off('disconnect');
    };
  }, []);

  const fetchMarkets = async (silent = false) => {
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      // Map sortBy to API order parameter
      const orderMap = {
        'volume': 'volume24hr',
        'ending': 'endDate',
        'liquidity': 'liquidity',
        'newest': 'startDate'
      };
      
      // Fetch regular markets
      const response = await api.get('/markets', {
        params: {
          limit: 200,
          active: true,
          order: orderMap[sortBy] || 'volume24hr',
          ascending: sortBy === 'ending'
        }
      });
      
      let filtered = response.data || [];
      
      // For crypto category, also fetch short-term markets (15min, hourly, 4h, daily)
      if (category === 'crypto') {
        try {
          const shortTermRes = await api.get('/markets/short-term-crypto');
          const shortTermMarkets = shortTermRes.data || [];
          
          // Merge and dedupe by conditionId
          const seen = new Set(filtered.map(m => m.conditionId));
          for (const m of shortTermMarkets) {
            if (m.conditionId && !seen.has(m.conditionId)) {
              seen.add(m.conditionId);
              filtered.push(m);
            }
          }
        } catch (e) {
          console.error('Failed to fetch short-term crypto:', e);
        }
      }
      
      // Client-side category filtering
      if (category === 'crypto') {
        filtered = filtered.filter(m => isCryptoMarket(m));
      } else if (category === 'sports') {
        filtered = filtered.filter(m => isSportsMarket(m));
      } else if (category === 'ai') {
        filtered = filtered.filter(m => isAIMarket(m));
      } else if (category === 'tech') {
        filtered = filtered.filter(m => isTechMarket(m));
      } else if (category === 'politics') {
        filtered = filtered.filter(m => isPoliticsMarket(m));
      } else if (category === 'finance' || category === 'economy') {
        filtered = filtered.filter(m => isFinanceMarket(m));
      } else if (category === 'geopolitics' || category === 'middle-east' || category === 'world') {
        filtered = filtered.filter(m => isGeopoliticsMarket(m, category));
      } else if (category === 'pop-culture') {
        filtered = filtered.filter(m => isPopCultureMarket(m));
      }
      
      // Client-side sorting for better accuracy
      if (sortBy === 'ending') {
        // Check all possible date field names from Gamma API
        const getEndDate = (m) => m.endDate || m.endDateIso || m.end_date_iso || m.end_date || 
          m.expirationDate || m.expiration_date || m.gameStartTime || m.game_start_time ||
          m.closeTime || m.close_time || m.resolutionDate || m.resolution_date;
        // Sort by end date - markets without dates go to the end, not filtered out
        filtered.sort((a, b) => {
          const dateA = getEndDate(a);
          const dateB = getEndDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1; // No date = sort to end
          if (!dateB) return -1;
          return new Date(dateA) - new Date(dateB);
        });
      } else if (sortBy === 'volume') {
        filtered.sort((a, b) => (b.volume24hr || 0) - (a.volume24hr || 0));
      } else if (sortBy === 'liquidity') {
        filtered.sort((a, b) => (b.liquidityNum || 0) - (a.liquidityNum || 0));
      }
      
      // Limit to 50 after filtering
      const finalMarkets = filtered.slice(0, 50);
      setMarkets(finalMarkets);
      
      // Subscribe to real-time price updates for visible markets
      const tokenIds = finalMarkets.flatMap(m => parseClobTokenIds(m.clobTokenIds)).filter(Boolean);
      if (tokenIds.length > 0) {
        socketManager.subscribeToMarkets(tokenIds);
      }
      
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch markets:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Manual refresh
  const handleManualRefresh = () => {
    fetchMarkets(true);
  };
  
  // Format last refresh time
  const formatLastRefresh = () => {
    if (!lastRefresh) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - lastRefresh) / 1000);
    if (diff < 5) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastRefresh.toLocaleTimeString();
  };

  // Handle real-time price updates from websocket
  const handlePriceUpdate = useCallback((data) => {
    const { tokenId, price } = data;
    if (!tokenId || price === undefined) return;
    
    setMarkets(prevMarkets => {
      return prevMarkets.map(market => {
        const tokens = parseClobTokenIds(market.clobTokenIds);
        const tokenIndex = tokens.indexOf(tokenId);
        if (tokenIndex === -1) return market;
        
        // Update the price for this outcome
        const prices = parseMaybeJsonArray(market.outcomePrices, [0.5, 0.5]);
        const newPrices = [...prices];
        newPrices[tokenIndex] = price;
        
        return {
          ...market,
          outcomePrices: newPrices,
          _lastUpdate: Date.now()
        };
      });
    });
  }, []);

  // Set up real-time price listener
  useEffect(() => {
    socketManager.connect();
    socketManager.on('marketPrice', handlePriceUpdate);
    
    return () => {
      socketManager.off('marketPrice', handlePriceUpdate);
    };
  }, [handlePriceUpdate]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchMarkets();
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.get('/markets/search', {
        params: { q: searchQuery }
      });
      setMarkets(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load market by URL or slug
  const handleLoadByUrl = async (e) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    
    setLoadingUrl(true);
    try {
      // Extract slug from URL or use as-is
      let slug = urlInput.trim();
      
      // Handle full Polymarket URLs
      if (slug.includes('polymarket.com')) {
        const match = slug.match(/polymarket\.com\/event\/([^/?#]+)/);
        if (match) slug = match[1];
      }
      
      // Handle condition IDs (0x...)
      if (slug.startsWith('0x')) {
        const response = await api.get(`/markets/by-condition/${slug}`);
        if (response.data) {
          setMarkets([response.data]);
          setSearchQuery('');
          setLoadingUrl(false);
          return;
        }
      }
      
      // Try to fetch by slug
      const response = await api.get(`/markets/by-slug/${slug}`);
      if (response.data) {
        // If it's an event with multiple markets, show all
        const marketsArray = Array.isArray(response.data) ? response.data : [response.data];
        setMarkets(marketsArray);
        setSearchQuery('');
      }
    } catch (error) {
      console.error('Failed to load market by URL:', error);
      alert('Could not find market. Try pasting the full Polymarket URL or event slug.');
    } finally {
      setLoadingUrl(false);
    }
  };
  
  // Calculate opportunity score for Goggles Mode
  // Scores markets based on REAL trading factors:
  // - Price edge (mispricing opportunities in 20-80% range)
  // - Liquidity (can you actually execute?)
  // - Volume (is there market activity?)
  // - Time to expiry (actionable timeframe?)
  // - Spread (tight spreads = fair pricing)
  const getOpportunityScore = (market) => {
    if (!gogglesMode) return null;
    
    const prices = parseMaybeJsonArray(market.outcomePrices, ['0.5', '0.5']);
    const price1 = parseFloat(prices[0]) || 0;
    const price2 = parseFloat(prices[1]) || 0;
    
    // Skip markets that are essentially decided (>90% or <10%)
    if (price1 < 0.10 || price1 > 0.90) return null;
    
    const volume = market.volume24hr || market.volumeNum || 0;
    const liquidity = market.liquidityNum || 0;
    const spread = market.spread || 0;
    
    // Skip low liquidity markets (can't execute trades effectively)
    if (liquidity < 1000) return null;
    
    let signals = [];
    let score = 0;
    
    // 1. EDGE ZONE: 20-40% or 60-80% prices have best risk/reward
    // Near 50% = too uncertain, near 0/100% = no upside
    const isEdgeZone = (price1 >= 0.20 && price1 <= 0.40) || (price1 >= 0.60 && price1 <= 0.80);
    if (isEdgeZone) {
      score += 25;
      signals.push('Good risk/reward zone');
    } else if (price1 >= 0.40 && price1 <= 0.60) {
      score += 15; // Coin flip - less predictable
      signals.push('High uncertainty');
    } else {
      score += 5;
    }
    
    // 2. LIQUIDITY: Higher = better execution
    if (liquidity >= 50000) {
      score += 25;
      signals.push('Deep liquidity');
    } else if (liquidity >= 10000) {
      score += 15;
      signals.push('Good liquidity');
    } else if (liquidity >= 3000) {
      score += 8;
    }
    
    // 3. VOLUME: Recent trading activity shows interest
    if (volume >= 100000) {
      score += 20;
      signals.push('High volume');
    } else if (volume >= 25000) {
      score += 12;
      signals.push('Active trading');
    } else if (volume >= 5000) {
      score += 5;
    }
    
    // 4. TIME TO EXPIRY: Short-term = more actionable
    if (market.endDate) {
      const hoursToEnd = (new Date(market.endDate) - new Date()) / 3600000;
      if (hoursToEnd > 0 && hoursToEnd <= 4) {
        score += 20;
        signals.push('Expires soon');
      } else if (hoursToEnd > 0 && hoursToEnd <= 24) {
        score += 15;
        signals.push('Expires today');
      } else if (hoursToEnd > 0 && hoursToEnd <= 72) {
        score += 8;
      }
    }
    
    // 5. SPREAD: Tight spread = efficient market
    if (spread > 0 && spread < 0.02) {
      score += 10;
      signals.push('Tight spread');
    } else if (spread > 0.05) {
      score -= 10; // Wide spread penalty
    }
    
    // Minimum threshold
    if (score < 35) return null;
    
    // Pick the most relevant signal for display
    const primarySignal = signals[0] || 'Trading opportunity';
    
    return {
      score: Math.round(score),
      rating: score >= 70 ? 'hot' : score >= 50 ? 'warm' : 'neutral',
      reason: primarySignal,
      signals
    };
  };

  // Filter markets - when Goggles Mode is ON, only show markets with real opportunities
  const filteredMarkets = markets.filter(m => {
    // Basic search filter
    if (searchQuery && !m.question?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // ALWAYS filter out expired markets
    if (m.endDate) {
      const endDate = new Date(m.endDate);
      if (endDate < new Date()) {
        return false; // Market has expired
      }
    }
    
    // Filter out resolved markets
    if (m.resolved === true || m.closed === true) {
      return false;
    }
    
    // Expiry filter - filter by time until expiry
    if (expiryFilter && m.endDate) {
      const endDate = new Date(m.endDate);
      const now = new Date();
      const hoursUntilExpiry = (endDate - now) / (1000 * 60 * 60);
      
      if (expiryFilter === '4h' && hoursUntilExpiry > 4) {
        return false;
      } else if (expiryFilter === '24h' && hoursUntilExpiry > 24) {
        return false;
      } else if (expiryFilter === '7d' && hoursUntilExpiry > 168) {
        return false;
      }
    } else if (expiryFilter && !m.endDate) {
      // If expiry filter is set but market has no end date, exclude it
      return false;
    }
    
    // When Goggles Mode is ON, filter out resolved/low-opportunity markets
    if (gogglesMode) {
      const prices = parseMaybeJsonArray(m.outcomePrices, ['0.5', '0.5']);
      const price1 = parseFloat(prices[0]) || 0;
      const price2 = parseFloat(prices[1]) || 0;
      
      // For binary markets, prices should sum to ~1.0
      // Filter out markets where either price is too extreme (< 15% or > 85%)
      // This means: if Yes is 1¢, No is 99¢ - FILTER OUT
      // If Yes is 20¢, No is 80¢ - KEEP (there's uncertainty)
      
      // Check if EITHER price is in the "decided" zone
      const isPrice1Extreme = price1 < 0.15 || price1 > 0.85;
      const isPrice2Extreme = price2 < 0.15 || price2 > 0.85;
      
      // If BOTH prices are extreme (one near 0, one near 1), it's decided
      if (isPrice1Extreme && isPrice2Extreme) {
        return false;
      }
      
      // Also filter if any single price is VERY extreme (< 5% or > 95%)
      if (price1 < 0.05 || price1 > 0.95 || price2 < 0.05 || price2 > 0.95) {
        return false;
      }
    }
    
    return true;
  });

  const handleTrade = (market, outcome, tokenId, price) => {
    if (!user?.wallet?.isConnected) {
      alert('Please connect your wallet in Settings first');
      return;
    }
    setTradingModal({ isOpen: true, market, outcome, tokenId, price });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Markets</h1>
          <p className="text-dark-400">Browse and trade prediction markets on Polymarket</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Live indicator */}
          <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${
            wsConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-dark-700 text-dark-400'
          }`}>
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'Live' : 'Offline'}
          </div>
          
          {/* Refresh button */}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 text-sm text-dark-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-dark-700"
            title="Refresh markets"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{formatLastRefresh()}</span>
          </button>
          
          {!user?.wallet?.isConnected && (
            <div className="text-sm text-amber-400 bg-amber-500/10 px-3 py-2 rounded-lg">
              Connect wallet to trade
            </div>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card space-y-4">
        {/* Main search */}
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search markets..."
              className="input pl-10"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input w-auto"
          >
            <option value="">All Categories</option>
            <option value="politics">Politics</option>
            <option value="sports">Sports</option>
            <option value="crypto">Crypto</option>
            <option value="pop-culture">Pop Culture</option>
            <option value="tech">Tech</option>
            <option value="ai">AI</option>
            <option value="finance">Finance</option>
            <option value="geopolitics">Geopolitics</option>
            <option value="middle-east">Middle East</option>
            <option value="economy">Economy</option>
            <option value="world">World</option>
          </select>
          <select
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value)}
            className="input w-auto"
          >
            <option value="">Any Expiry</option>
            <option value="4h">≤ 4 Hours</option>
            <option value="24h">≤ 24 Hours</option>
            <option value="7d">≤ 7 Days</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="input w-auto"
          >
            <option value="volume">Highest Volume</option>
            <option value="liquidity">Highest Liquidity</option>
            <option value="ending">Ending Soonest</option>
          </select>
          <button type="submit" className="btn btn-primary">
            Search
          </button>
        </form>
        
        {/* Load by URL */}
        <form onSubmit={handleLoadByUrl} className="flex gap-3">
          <div className="relative flex-1">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="Paste Polymarket URL or event slug (e.g. bitcoin-above-88k-on-december-29)"
              className="input pl-10 pr-10"
            />
            {urlInput && (
              <button
                type="button"
                onClick={() => setUrlInput('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-dark-600 rounded transition-colors"
                title="Clear"
              >
                <X className="w-4 h-4 text-dark-400 hover:text-white" />
              </button>
            )}
          </div>
          <button 
            type="submit" 
            className="btn btn-secondary"
            disabled={loadingUrl}
          >
            {loadingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load Market'}
          </button>
        </form>
        
        {/* Goggles Mode Toggle */}
        {isTierLoading ? (
          <div className="flex items-center gap-2 pt-2 border-t border-dark-700 text-dark-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading subscription...</span>
          </div>
        ) : hasGogglesAccess ? (
          <div className="flex items-center justify-between pt-2 border-t border-dark-700">
            <div className="flex items-center gap-2">
              <Glasses className="w-5 h-5 text-primary-400" />
              <div>
                <span className="font-medium">Goggles Mode</span>
                <p className="text-xs text-dark-400">Highlights best trading opportunities</p>
              </div>
            </div>
            <button
              onClick={() => setGogglesMode(!gogglesMode)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                gogglesMode ? 'bg-primary-600' : 'bg-dark-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  gogglesMode ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-2 border-t border-dark-700 text-dark-400">
            <Glasses className="w-5 h-5" />
            <span className="text-sm">Upgrade to Basic+ for Goggles Mode - highlights best opportunities</span>
          </div>
        )}
      </div>

      {/* Markets Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
        </div>
      ) : filteredMarkets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMarkets.map(market => (
            <MarketCard 
              key={market.id || market.conditionId} 
              market={market} 
              onTrade={handleTrade}
              opportunityScore={getOpportunityScore(market)}
              gogglesMode={gogglesMode}
            />
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <TrendingUp className="w-12 h-12 mx-auto mb-4 text-dark-500" />
          <h3 className="text-lg font-medium mb-2">No Markets Found</h3>
          <p className="text-dark-400">Try adjusting your search or filters.</p>
        </div>
      )}

      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModal.isOpen}
        onClose={() => setTradingModal({ isOpen: false, market: null, outcome: null, tokenId: null, price: null })}
        market={tradingModal.market}
        outcome={tradingModal.outcome}
        tokenId={tradingModal.tokenId}
        currentPrice={tradingModal.price}
      />
    </div>
  );
}
